import { Device, Trip, TripAnnotation, User } from "@fairfleet/geotab";
import { REASON_NA } from "@/constants/constants";
import { tripInterval } from "@/utilities/trip/tripInterval";

/** A vehicle's annotations by time, each with its place in the original list. */
interface TimedAnnotation {
  time: number;
  order: number;
  annotation: TripAnnotation;
}

export interface FilteredTrip extends Trip {
  /**
   * The trip's reason text, `undefined` when it has none, `false` while the reasons load and
   * `null` when they could not be read (failed or capped): unknown, never "no reason".
   */
  reason?: string | false | null;
}

/** Whether the trip's reason is known, so it can be shown as is and safely overwritten. */
export function isReasonRead(trip: Pick<FilteredTrip, "reason">): boolean {
  return trip.reason !== false && trip.reason !== null;
}

export function buildFilteredTrips(
  trips: Trip[],
  lookups: {
    annotations?: TripAnnotation[];
    devices?: Device[];
    users?: User[];
    reasons: string[];
    annotationsPending?: boolean;
    /** Vehicles whose annotations failed to load; their trips get `reason: null`. */
    unreadDeviceIds?: ReadonlySet<string>;
  },
): FilteredTrip[] {
  const { annotations, devices, users, reasons, annotationsPending, unreadDeviceIds } = lookups;
  const isNa = reasons.includes(REASON_NA);

  // Sorted by time so a trip finds its annotation by binary search. Scanning every annotation of
  // the vehicle for every trip froze the tab on a long range: a year of a busy vehicle is millions
  // of comparisons.
  const annotationsByDevice = new Map<string, TimedAnnotation[]>();
  if (annotations) {
    annotations.forEach((a, order) => {
      const entry = { time: new Date(a.dateTime).getTime(), order, annotation: a };
      // Without a readable time it falls in no trip, and in a sorted list it would break the search.
      if (Number.isNaN(entry.time)) return;
      const list = annotationsByDevice.get(a.device.id);
      if (list) {
        list.push(entry);
      } else {
        annotationsByDevice.set(a.device.id, [entry]);
      }
    });
    for (const list of annotationsByDevice.values()) {
      list.sort((a, b) => a.time - b.time);
    }
  }

  const devicesById = new Map<string, Device>();
  if (devices) {
    for (const d of devices) {
      devicesById.set(d.id, d);
    }
  }

  const usersById = new Map<string, User>();
  if (users) {
    for (const u of users) {
      usersById.set(u.id, u);
    }
  }

  const result: FilteredTrip[] = [];

  for (const trip of trips) {
    const deviceAnnotations = annotationsByDevice.get(trip.device.id);
    const annotation = deviceAnnotations && annotationWithin(deviceAnnotations, trip);

    const unread = !annotationsPending && !!unreadDeviceIds?.has(trip.device.id);
    // A reason filter cannot be decided without the reason; the page names the failed load.
    if (unread && (isNa || reasons.length > 0)) continue;

    if (!annotationsPending && !unread) {
      if (isNa) {
        if (annotation) continue;
      } else if (reasons.length > 0) {
        if (!annotation) continue;
        if (!reasons.includes(annotation.text)) continue;
      }
    }

    result.push({
      ...trip,
      reason: annotationsPending ? false : unread ? null : annotation?.text,
      device: devicesById.get(trip.device?.id) ?? trip.device,
      driver: usersById.get(trip.driver?.id) ?? trip.driver,
    } as FilteredTrip);
  }

  return result;
}

/**
 * The annotation made during the trip, start and end included. Of several, the one that came first
 * in the list from Geotab, as a plain search through that list would find it.
 */
function annotationWithin(sorted: TimedAnnotation[], trip: Trip): TripAnnotation | undefined {
  const interval = tripInterval(trip);
  const bounds = [new Date(interval.start).getTime(), new Date(interval.end).getTime()];
  // A trip ending before it starts is searched the other way round, as date-fns' interval check did.
  const from = Math.min(bounds[0], bounds[1]);
  const to = Math.max(bounds[0], bounds[1]);

  let low = 0;
  let high = sorted.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    if (sorted[middle].time < from) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }

  let first: TimedAnnotation | undefined;
  for (let index = low; index < sorted.length && sorted[index].time <= to; index++) {
    if (!first || sorted[index].order < first.order) {
      first = sorted[index];
    }
  }
  return first?.annotation;
}
